import React, { useState } from 'react';
import {
    Drawer,
    Descriptions,
    Tag,
    Timeline,
    Typography,
    Space,
    Button,
    Input,
    Select,
    DatePicker,
    Divider,
    Empty,
    Spin,
    message,
} from 'antd';
import {
    PhoneOutlined,
    FileTextOutlined,
    SwapRightOutlined,
    ClockCircleOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
    addPreRegistrationActivity,
    fetchPreRegistrationById,
} from '../../store/slices/studentParentsSlice';
import {
    STATUS_META,
    SOURCE_META,
    ACTIVITY_TYPE,
    ACTIVITY_META,
} from './constants';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const activityIcon = (type) => {
    switch (type) {
        case ACTIVITY_TYPE.Call:
            return <PhoneOutlined />;
        case ACTIVITY_TYPE.StatusChange:
            return <SwapRightOutlined />;
        case ACTIVITY_TYPE.FollowUp:
            return <ClockCircleOutlined />;
        default:
            return <FileTextOutlined />;
    }
};

const activityColor = (type) => ACTIVITY_META[type]?.color || 'gray';

const PreRegistrationDetailDrawer = ({ open, detail, loading, onClose, onChanged }) => {
    const dispatch = useDispatch();
    const [activityType, setActivityType] = useState(ACTIVITY_TYPE.Call);
    const [activityContent, setActivityContent] = useState('');
    const [followUpDate, setFollowUpDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const resetActivityForm = () => {
        setActivityType(ACTIVITY_TYPE.Call);
        setActivityContent('');
        setFollowUpDate(null);
    };

    const handleAddActivity = async () => {
        if (!activityContent.trim()) {
            message.warning('Lütfen bir not / açıklama girin');
            return;
        }
        setSubmitting(true);
        try {
            await dispatch(
                addPreRegistrationActivity({
                    preRegistrationId: detail.id,
                    type: activityType,
                    content: activityContent.trim(),
                    followUpDate:
                        activityType === ACTIVITY_TYPE.FollowUp && followUpDate
                            ? followUpDate.toISOString()
                            : null,
                })
            ).unwrap();
            message.success('Aktivite eklendi');
            resetActivityForm();
            // Detayı tazele
            await dispatch(fetchPreRegistrationById({ id: detail.id }));
            onChanged?.();
        } catch (error) {
            message.error('Aktivite eklenirken bir hata oluştu');
        } finally {
            setSubmitting(false);
        }
    };

    const statusMeta = detail ? STATUS_META[detail.status] : null;

    return (
        <Drawer
            title={detail ? detail.parentFullName : 'Ön Kayıt Detayı'}
            width={520}
            open={open}
            onClose={onClose}
            destroyOnClose
        >
            {loading || !detail ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Space style={{ marginBottom: 16 }}>
                        {statusMeta && <Tag color={statusMeta.tag}>{statusMeta.label}</Tag>}
                        <Tag>{SOURCE_META[detail.source]}</Tag>
                        <Button
                            type="primary"
                            size="small"
                            icon={<PhoneOutlined />}
                            href={`tel:${detail.parentPhoneNumber}`}
                        >
                            Ara
                        </Button>
                    </Space>

                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Veli">{detail.parentFullName}</Descriptions.Item>
                        <Descriptions.Item label="Telefon">
                            <Text copyable>{detail.parentPhoneNumber}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Öğrenci">{detail.studentFullName}</Descriptions.Item>
                        <Descriptions.Item label="Doğum Tarihi">
                            {detail.studentBirthDate
                                ? `${dayjs(detail.studentBirthDate).format('DD/MM/YYYY')} (${dayjs().diff(
                                      dayjs(detail.studentBirthDate),
                                      'year'
                                  )} yaş)`
                                : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Başvuru Tarihi">
                            {dayjs(detail.createdDate).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        {detail.followUpDate && (
                            <Descriptions.Item label="Tekrar Aranacak">
                                {dayjs(detail.followUpDate).format('DD/MM/YYYY')}
                            </Descriptions.Item>
                        )}
                        {detail.notConvertedReason && (
                            <Descriptions.Item label="Dönmeme Nedeni">
                                {detail.notConvertedReason}
                            </Descriptions.Item>
                        )}
                        {detail.notes && (
                            <Descriptions.Item label="Genel Not">{detail.notes}</Descriptions.Item>
                        )}
                    </Descriptions>

                    <Divider orientation="left" style={{ marginTop: 24 }}>
                        Aktivite / Not Ekle
                    </Divider>
                    <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                        <Select
                            value={activityType}
                            style={{ width: 140 }}
                            onChange={setActivityType}
                            options={[
                                { value: ACTIVITY_TYPE.Call, label: 'Arama' },
                                { value: ACTIVITY_TYPE.Note, label: 'Not' },
                                { value: ACTIVITY_TYPE.FollowUp, label: 'Takip' },
                            ]}
                        />
                        {activityType === ACTIVITY_TYPE.FollowUp && (
                            <DatePicker
                                style={{ width: 160 }}
                                placeholder="Tekrar aranacak"
                                format="DD/MM/YYYY"
                                value={followUpDate}
                                onChange={setFollowUpDate}
                            />
                        )}
                    </Space.Compact>
                    <TextArea
                        rows={2}
                        placeholder="Görüşme notu, açıklama..."
                        value={activityContent}
                        onChange={(e) => setActivityContent(e.target.value)}
                        style={{ marginBottom: 8 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        loading={submitting}
                        onClick={handleAddActivity}
                        block
                    >
                        Ekle
                    </Button>

                    <Divider orientation="left" style={{ marginTop: 24 }}>
                        Geçmiş ({detail.activities?.length || 0})
                    </Divider>
                    {detail.activities && detail.activities.length > 0 ? (
                        <Timeline
                            items={detail.activities.map((a) => ({
                                dot: activityIcon(a.type),
                                color: activityColor(a.type),
                                children: (
                                    <div>
                                        <Space size={4} wrap>
                                            <Tag color={ACTIVITY_META[a.type]?.color}>
                                                {ACTIVITY_META[a.type]?.label}
                                            </Tag>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {dayjs(a.createdDate).format('DD/MM/YYYY HH:mm')}
                                            </Text>
                                        </Space>
                                        {a.type === ACTIVITY_TYPE.StatusChange && (
                                            <div style={{ margin: '4px 0' }}>
                                                <Tag color={STATUS_META[a.oldStatus]?.tag}>
                                                    {STATUS_META[a.oldStatus]?.label || '-'}
                                                </Tag>
                                                <SwapRightOutlined />{' '}
                                                <Tag color={STATUS_META[a.newStatus]?.tag}>
                                                    {STATUS_META[a.newStatus]?.label || '-'}
                                                </Tag>
                                            </div>
                                        )}
                                        {a.content && (
                                            <Paragraph style={{ margin: '4px 0 0' }}>{a.content}</Paragraph>
                                        )}
                                    </div>
                                ),
                            }))}
                        />
                    ) : (
                        <Empty description="Henüz aktivite yok" />
                    )}
                </>
            )}
        </Drawer>
    );
};

export default PreRegistrationDetailDrawer;
